/**
 * Sentry bootstrap for admin-v2.
 *
 * Imported for its side effect as the very first import in `main.tsx`, so error
 * and performance monitoring is active before the rest of the app loads. The
 * effective on/off switch is the DSN: it is injected per environment at build
 * time (see `build.sh`) and left empty locally, so `Sentry.init` is a no-op in
 * development. Unlike the other frontends this app uses TanStack Router, so the
 * plain browser tracing integration is used rather than a react-router one.
 */
import * as Sentry from "@sentry/react";

Sentry.init({
  debug:
    import.meta.env.MODE === "acceptance" ||
    import.meta.env.MODE === "go" ||
    import.meta.env.MODE === "development",
  dsn: import.meta.env.VITE_SENTRY_DSN, // Set in build.sh
  environment: "admin-v2",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.captureConsoleIntegration({
      levels: ["error", "warn"],
    }),
  ],

  tracesSampleRate: 1.0, // Capture 100% of transactions

  replaysSessionSampleRate: 0.1, // Capture Replay for 10% of all sessions,
  replaysOnErrorSampleRate: 1.0, // Capture Replay for 100% of errors
});

// Report failed resource loads (images, stylesheets), which do not bubble as
// regular exceptions. Based on
// https://docs.sentry.io/platforms/javascript/guides/react/troubleshooting/#capturing-resource-404s
document.body.addEventListener(
  "error",
  (event) => {
    if (!event.target) return;

    if ((event.target as HTMLElement).tagName === "IMG") {
      Sentry.captureException(
        new Error(
          `Failed to load image: ${(event.target as HTMLImageElement).src}`,
        ),
      );
      return;
    }

    if ((event.target as HTMLElement).tagName === "LINK") {
      Sentry.captureException(
        new Error(
          `Failed to load css: ${(event.target as HTMLLinkElement).href}`,
        ),
      );
      return;
    }
  },
  true, // useCapture - necessary for resource loading errors
);
